import type { DetailPageConfig } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Time_Entries } from "@/src/gql/graphql";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";
import { timeEntryDetailActions } from "@/lib/actions/definitions/entities";

// ============================================================================
// TIME ENTRY DETAIL PAGE CONFIGURATION
// ============================================================================

export const TIME_ENTRY_DETAIL_CONFIG: DetailPageConfig<Time_Entries> = {
  entity: "time_entries",
  displayName: "Time Entry",

  // Tab configurations
  tabs: [
    {
      id: "details",
      label: "Details",
      icon: "file-text",
      variant: "blue",
      group: "core",
      priority: 1,
      clientVisible: true,
      content: {
        type: "custom",
        customComponent: "TimeEntryDetails",
      },
    },
    {
      id: "ticket",
      label: "Ticket",
      icon: "ticket",
      variant: "purple",
      group: "related",
      priority: 2,
      clientVisible: true,
      // Always show Ticket tab so users can inspect ticket context even when
      // related ticket join data is missing for a specific time entry.
      visible: () => true,
      content: {
        type: "custom",
        customComponent: "TicketDetailsTab",
      },
    },
    {
      id: "contact",
      label: "Contact",
      icon: "user",
      variant: "green",
      group: "related",
      priority: 3,
      visible: (item: Time_Entries) => item != null && !!(item as any).contacts,
      content: {
        type: "custom",
        customComponent: "ContactDetailsTab",
      },
    },
    {
      id: "company",
      label: "Company",
      icon: "building",
      variant: "red",
      group: "related",
      priority: 4,
      content: {
        type: "custom",
        customComponent: "CompanyDetailsTab",
      },
    },
    {
      id: "activity",
      label: "Activity",
      icon: "activity",
      variant: "orange",
      group: "activity",
      priority: 5,
      content: {
        type: "custom",
        customComponent: "TimeEntryActivityTimeline",
      },
    },
  ],

  // Action configurations - from centralized registry
  actions: timeEntryDetailActions,

  // Breadcrumb configuration
  breadcrumbs: {
    show: true,
    items: [
      {
        label: "Time Entries",
        href: "/time_entries",
      },
      {
        label: "Details",
        href: (entry: Time_Entries) => `/time_entries/${entry.id}`,
        current: true,
      },
    ],
  },

  // Page configuration
  defaultTab: "details",
  showHeader: true,
  showBreadcrumbs: true,
  showActions: true,

  // Title and description templates
  // Uses denormalized ticket_number, ticket_title, company_name (no joins)
  titleTemplate: (item: Time_Entries) => {
    if (!item.date_worked) return "Time Entry";
    const date = formatDate(item.date_worked);
    const hours = `${item.hours || 0}h`;

    // Build title with primary info (date and hours)
    let title = `${date} • ${hours}`;

    // Add ticket information from denormalized columns
    if (item.ticket_number != null || item.ticket_title) {
      const ticketNumber = item.ticket_number != null
        ? `WTC-${String(item.ticket_number).padStart(6, "0")}`
        : null;

      if (ticketNumber && item.ticket_title) {
        const ticketTitle =
          item.ticket_title.length > 35
            ? `${item.ticket_title.substring(0, 35)}...`
            : item.ticket_title;
        title += ` • ${ticketNumber}: ${ticketTitle}`;
      } else if (ticketNumber) {
        title += ` • ${ticketNumber}`;
      }
    }

    // Add company information from denormalized column
    if (item.company_name) {
      title += ` • ${item.company_name}`;
    }

    return title;
  },

  descriptionTemplate: (item: Time_Entries) => {
    const parts: string[] = [];

    // Status badge with emoji for visual clarity
    const statusEmoji = item.is_approved ? "✓" : "⏳";
    const status = item.is_approved ? "Approved" : "Pending Approval";
    parts.push(`${statusEmoji} ${status}`);

    // Billable status with emoji
    const billableEmoji = item.entry_type === "billable" ? "💰" : "🔧";
    const billable = item.entry_type === "billable" ? "Billable" : "Non-billable";
    parts.push(`${billableEmoji} ${billable}`);

    // Add entry type if it's not the standard billable/non-billable
    if (item.entry_type && item.entry_type !== "billable" && item.entry_type !== "non_billable") {
      const typeLabel =
        item.entry_type.replace("_", " ").charAt(0).toUpperCase() + item.entry_type.slice(1);
      parts.push(`📌 ${typeLabel}`);
    }

    return parts.join(" • ");
  },
};
